















































import {
  computed,
  defineComponent,
  onMounted,
  ref,
  ssrRef,
  useFetch,
  provide,
} from '@nuxtjs/composition-api';
import { CacheTagPrefix, useCache } from '@vue-storefront/cache';
import { usePageStore } from '~/stores/page';
import {
  useCategory,
  useFacet,
  useUiHelpers,
  useUiState,
} from '~/composables';

import { useAddToCart } from '~/helpers/cart/addToCart';
import { useWishlist } from '~/modules/wishlist/composables/useWishlist';
import { usePrice } from '~/modules/catalog/pricing/usePrice';
import { useCategoryContent } from '~/modules/catalog/category/components/cms/useCategoryContent';
import { useTraverseCategory } from '~/modules/catalog/category/helpers/useTraverseCategory';
import facetGetters from '~/modules/catalog/category/getters/facetGetters';
import { getMetaInfo } from '~/helpers/getMetaInfo';
import { getCategoryMetaTitleSuffix } from '~/modules/catalog/helpers/getCategoryMetaTitleSuffix';

import CategoryNavbar from '~/modules/catalog/category/components/navbar/CategoryNavbar.vue';
import CategoryBreadcrumbs from '~/modules/catalog/category/components/breadcrumbs/CategoryBreadcrumbs.vue';
import ShowMore from '~/modules/catalog/category/components/pagination/ShowMore.vue';

import type { ProductInterface } from '~/modules/GraphQL/types';
import type { Pagination } from '~/composables/types';
import type { Product } from '~/modules/catalog/product/types';

import Banner from '~/components/Home/Banner.vue';
import { useFilters } from '~/modules/catalog/category/components/filters/useFilters';
import { useYoastSeo } from '~/modules/yoastSeo/composables/useYoastSeo';
import RecentlyViewedProducts from '~/modules/catalog/category/components/views/RecentlyViewedProducts.vue';
import { getCategoryBannerSlides } from '~/modules/catalog/helpers/getCategoryBannerSlides';

const isServer = typeof window === 'undefined';

export default defineComponent({
  name: 'CategoryPage',
  components: {
    CategoryEmptyResults: () => import('~/modules/catalog/category/components/CategoryEmptyResults.vue'),
    CategoryFilters: () => import('~/modules/catalog/category/components/filters/CategoryFilters.vue'),
    CategoryFiltersSidebar: () => import('~/modules/catalog/category/components/filters/CategoryFiltersSidebar.vue'),
    MainFilter: () => import('~/modules/catalog/category/components/filters/MainFilter.vue'),
    CmsContent: () => import('~/modules/catalog/category/components/cms/CmsContent.vue'),
    CategoryProductGrid: () => import('~/modules/catalog/category/components/views/CategoryProductGrid.vue'),
    CategoryNavbar,
    CategoryBreadcrumbs,
    ShowMore,
    Banner,
    RecentlyViewedProducts,
  },
  transition: 'fade',
  setup() {
    const { routeData } = usePageStore();
    const { getContentData } = useCategoryContent();
    const { loadCategoryMeta } = useCategory();
    const { addTags } = useCache();
    const { loadMeta } = useYoastSeo();
    const uiHelpers = useUiHelpers();
    const cmsContent = ref('');
    const isShowCms = ref(false);
    const isShowProducts = ref(false);
    const products = ssrRef<ProductInterface[]>([]);
    const pagination = ref<Pagination>({});
    const totalItems = ref();
    const productContainerElement = ref<HTMLElement | null>(null);

    const {
      changeToCategoryListView,
      changeToCategoryGridView,
      isDesktopFiltersOpen,
    } = useUiState();
    const {
      loadWithAllItems: loadWishlist,
      addItem: addItemToWishlistBase,
      isInWishlist,
      removeItem: removeItemFromWishlist,
    } = useWishlist();
    const { result, search, loading: facetLoading } = useFacet();
    const { addItemToCart } = useAddToCart();

    const categoryMeta = ref(null);

    const addItemToWishlist = async (product: Product) => {
      await (isInWishlist({ product })
        ? removeItemFromWishlist({ product })
        : addItemToWishlistBase({ product }));
    };

    const { activeCategory: category, loadCategoryTree } = useTraverseCategory();
    const categoryName = computed(() => category.value?.name ?? '');
    const categoryDescription = computed(() => category.value?.description ?? '');
    const slides = computed(() => getCategoryBannerSlides(category.value));
    const categoryImage = computed(() => category.value?.image ?? '/general/404.jpeg');
    const categoryImageConfig = {
      loading: 'lazy',
      format: 'webp',
      width: 1200,
      height: 600,
      placeholder: [120, 60],
    };

    const categoryUid = routeData.uid;

    const { fetch } = useFetch(async () => {
      if (!category.value) {
        await loadCategoryTree();
      }

      const [content, categoryMetaData] = await Promise.all([
        getContentData(categoryUid as string),
        loadCategoryMeta({ category_uid: categoryUid }),
        search({ ...uiHelpers.getFacetsFromURL(), category_uid: categoryUid }),
      ]);

      categoryMeta.value = categoryMetaData;
      cmsContent.value = content?.cmsBlock?.content ?? '';
      isShowCms.value = content.isShowCms;
      isShowProducts.value = content.isShowProducts;

      products.value = facetGetters.getProducts(result.value) ?? [];
      pagination.value = facetGetters.getPagination(result.value);
      totalItems.value = result.value?.data?.total || 0;

      const tags = [{ prefix: CacheTagPrefix.View, value: routeData.uid }];
      const productTags = products.value.map((product) => ({
        prefix: CacheTagPrefix.Product,
        value: product.uid,
      }));

      addTags([...tags, ...productTags]);
    });

    const customMeta = ref(null);
    useFetch(async () => {
      customMeta.value = await loadMeta('catalog_category', 'uid', categoryUid);
    });

    const isPriceLoaded = ref(false);

    onMounted(async () => {
      console.log('Category Banner Slides', slides.value);
      loadWishlist();
      const { getPricesBySku } = usePrice();
      if (products.value.length > 0) {
        const skus = products.value.map((item) => item.sku);
        const priceData = await getPricesBySku(skus, pagination.value.itemsPerPage);
        products.value = products.value.map((product) => {
          const priceRange = priceData.items.find((item) => item.sku === product.sku)?.price_range;

          if (priceRange) {
            return {
              ...product,
              price_range: priceRange,
            };
          }

          return { ...product };
        });
      }

      isPriceLoaded.value = true;
    });

    const goToPage = (page: number) => {
      uiHelpers.changePage(page, false);
      fetch();
    };

    const showMore = async () => {
      await search({ ...uiHelpers.getFacetsFromURL(), category_uid: categoryUid });
      products.value = [...products.value, ...(facetGetters.getProducts(result.value) ?? [])];
      pagination.value = facetGetters.getPagination(result.value);
    };

    const doChangeItemsPerPage = (itemsPerPage: number) => {
      uiHelpers.changeItemsPerPage(itemsPerPage, false);
      goToPage(0);
    };

    const onReloadProducts = () => {
      goToPage(0);
      // productContainerElement.value.scrollIntoView();
    };

    const {
      selectedFilters, removableFilters, filters, catUid, catPath, isLoading,
      selectFilter, doRemoveFilter, isFilterSelected, doApplyFilters, doClearFilters,
      loadedFilters, loadFilters
    } = useFilters();
    catUid.value = routeData.uid;
    catPath.value = routeData.path;
    const doApplyFiltersMod = () => {
      doApplyFilters();
      onReloadProducts();
    };
    const doRemoveFilterMod = ({ id, value }: { id: string, value: string }) => {
      doRemoveFilter({ id, value });
      onReloadProducts();
    };
    const doClearFiltersMod = () => {
      doClearFilters();
      onReloadProducts();
    };
    provide('UseFiltersProvider', {
      selectedFilters, removableFilters, filters, catUid, catPath, isLoading,
      selectFilter, isFilterSelected, isFilterDisabled: () => false,
      doRemoveFilter: doRemoveFilterMod, doApplyFilters: doApplyFiltersMod, doClearFilters: doClearFiltersMod,
    });

    if (isServer) {
      useFetch(async () => await loadFilters()) // this is needed for meta title
    }

    return {
      isPriceLoaded,
      ...uiHelpers,
      isDesktopFiltersOpen,
      changeToCategoryListView,
      changeToCategoryGridView,
      addItemToCart,
      addItemToWishlist,
      pagination,
      products,
      isShowCms,
      isShowProducts,
      cmsContent,
      category,
      categoryName,
      categoryDescription,
      categoryImage,
      categoryImageConfig,
      routeData,
      doChangeItemsPerPage,
      productContainerElement,
      categoryMeta,
      onReloadProducts,
      goToPage,
      showMore,
      totalItems,
      customMeta,
      filters,
      loadedFilters,
      facetLoading,
    };
  },
  head() {
    const metaInfo = getMetaInfo(this.categoryMeta);
    if (metaInfo) {
      const titleSuffix = getCategoryMetaTitleSuffix(this.resolveQuery(), isServer ? this.loadedFilters : this.filters, this.pagination);
      metaInfo.title += titleSuffix;
      if (this.customMeta) {
        metaInfo.meta.push(...this.customMeta);
      }
      const titleMeta = metaInfo.meta.find(item => item.name === 'title');
      if (titleMeta) {
        titleMeta.content += titleSuffix;
      } else {
        metaInfo.meta.push({
          hid: 'title',
          name: 'title',
          content: metaInfo.title,
        })
      }
    }
    return metaInfo;
  },
});
